/**
 * Impact Analysis API
 *
 * Wraps `/api/v1/impact-analysis/{sessionId}/*` endpoints — threat chain
 * fetch, single-control evaluation, save-controls, and delete. The shared
 * axios client has baseURL `/api/v1`.
 */

import client from './client';

/**
 * Fetch the threat-centric chain for impact analysis.
 * Mirrors AthenaBackendService.getThreatChain(sessionId).
 *
 * Preserves the legacy contract: returns `[]` on 404 or an empty/non-JSON
 * body. Other errors propagate via the interceptor's structured rejection.
 */
export async function getThreatChain(sessionId) {
  try {
    const result = await client.get(
      `/impact-analysis/${sessionId}/threat-chain`
    );
    return result ?? [];
  } catch (error) {
    if (error?.status === 404) return [];
    throw error;
  }
}

/**
 * Evaluate a single control for a single threat (synchronous ~15–30s).
 * Mirrors AthenaBackendService.evaluateThreatControl(sessionId, threatId, control).
 */
export async function evaluateThreatControl(sessionId, threatId, control) {
  return client.post(`/impact-analysis/${sessionId}/evaluate-control`, {
    threat_id: threatId,
    control,
  });
}

/**
 * Save user-modified controls for impact analysis.
 * Mirrors AthenaBackendService.saveImpactControls(sessionId, threats).
 */
export async function saveImpactControls(sessionId, threats) {
  return client.post(`/impact-analysis/${sessionId}/save-controls`, { threats });
}

/**
 * Delete the impact analysis for a session.
 * Mirrors AthenaBackendService.deleteImpactAnalysis(sessionId).
 */
export async function deleteImpactAnalysis(sessionId) {
  return client.delete(`/impact-analysis/${sessionId}`);
}
